import React, { useRef, useEffect, useState, memo } from 'react';
import { hasZeroFindings } from '../utils/findingsUtils';
import { isAttestationEnabled } from '../utils/attestationUtils';
import { ReportData } from '../types/report.types';

interface EditorNavigationProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  reportData?: ReportData;
  userRole?: string;
}

const template1Sections = [
  { id: 'branding', label: 'Branding', icon: '🏷️' },
  { id: 'cover', label: 'Cover Page', icon: '📄' },
  { id: 'disclaimer', label: 'Disclaimer', icon: '🔍' },
  { id: 'document_reference', label: 'Document Reference', icon: '📋' },
  { id: 'executive_summary', label: 'Executive Summary', icon: '📝' },
  { id: 'recommendations', label: 'Recommendations', icon: '💡' },
  { id: 'scope', label: 'Scope', icon: '🎯' },
  { id: 'project_objectives', label: 'Project Objectives', icon: '🎯' },
  { id: 'findings_summary', label: 'Findings Summary', icon: '📊' },
  { id: 'vulnerability_ratings', label: 'Vulnerability Ratings', icon: '🔍' },
  { id: 'key_findings', label: 'Key Findings', icon: '🔑' },
  { id: 'finding_config', label: 'Finding Config', icon: '⚙️' },
  { id: 'critical_findings', label: 'Critical Findings', icon: '⚠️' },
  { id: 'high_findings', label: 'High Findings', icon: '🔴' },
  { id: 'medium_findings', label: 'Medium Findings', icon: '🟡' },
  { id: 'low_findings', label: 'Low Findings', icon: '🟢' },
  { id: 'info_findings', label: 'Info Findings', icon: 'ℹ️' },
  { id: 'methodology_selector', label: 'Methodology Selection', icon: '🧩' },
  { id: 'attestation_letter', label: 'Letter of Attestation', icon: '📜' },
];

const template2Sections = [
  { id: 'branding', label: 'Branding', icon: '🏷️' },
  { id: 'cover', label: 'Cover Page V2', icon: '📄' },
  { id: 'executive_summary', label: 'Executive Summary V2', icon: '📝' },
  { id: 'scope', label: 'Scope V2', icon: '🎯' },
  { id: 'methodology', label: 'Methodology V2', icon: '📖' },
  { id: 'owasp_top_10', label: 'OWASP Top 10 Coverage', icon: '🔒' },
  { id: 'vulnerability_ratings', label: 'Vulnerability Ratings V2', icon: '🔍' },
  { id: 'findings_summary', label: 'Findings Summary V2', icon: '📊' },
  { id: 'finding_config', label: 'Finding Config', icon: '⚙️' },
  { id: 'critical_findings', label: 'Critical Findings', icon: '⚠️' },
  { id: 'high_findings', label: 'High Findings', icon: '🔴' },
  { id: 'medium_findings', label: 'Medium Findings', icon: '🟡' },
  { id: 'low_findings', label: 'Low Findings', icon: '🟢' },
  { id: 'info_findings', label: 'Info Findings', icon: 'ℹ️' },
  { id: 'remediation_roadmap', label: 'Remediation Roadmap V2', icon: '🛣️' },
  { id: 'conclusion', label: 'Conclusion V2', icon: '🏁' },
  { id: 'tools_approach', label: 'Tools & Approach V2', icon: '🛠️' },
  { id: 'attestation_letter', label: 'Letter of Attestation', icon: '📜' },
];

// Sections to hide when there are zero findings
const findingsRelatedSections = [
  'findings_summary',
  'key_findings',
  'critical_findings',
  'high_findings',
  'medium_findings',
  'low_findings',
  'info_findings'
];

const EditorNavigation: React.FC<EditorNavigationProps> = memo(({ activeSection, onSectionChange, reportData, userRole }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeButtonRef = useRef<HTMLButtonElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Filter sections based on whether there are findings and user role
  const isZeroFindings = hasZeroFindings(reportData);
  const activeTemplateList = reportData?.template === 'template-2' ? template2Sections : template1Sections;
  const sections = activeTemplateList.filter(section => {
    // Hide findings related sections if zero findings
    if (isZeroFindings && findingsRelatedSections.includes(section.id)) return false;
    
    // Role based filtering for finding_config
    if (section.id === 'finding_config') {
      const role = userRole?.toUpperCase();
      return ['ADMIN', 'QA', 'SUB_ADMIN', 'ADMIN_MANAGER'].includes(role || '');
    }
    
    return true;
  });

  // Auto-scroll to active section
  useEffect(() => {
    if (activeButtonRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const button = activeButtonRef.current;
      const containerWidth = container.offsetWidth;
      const buttonLeft = button.offsetLeft;
      const buttonWidth = button.offsetWidth;
      const scrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }, [activeSection]);

  // Update scroll arrow state
  const updateScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft + container.offsetWidth < container.scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollButtons();
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, []);

  // Handle wheel events for horizontal scrolling
  const handleWheel = (e: React.WheelEvent) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += e.deltaY;
    }
  };

  // Arrow scroll handlers
  const scrollBy = (amount: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  return (
    <div className="sticky top-0 z-10 w-full px-1 py-1 bg-blue-800 border-b border-blue-900 flex items-center">
      {/* Left Arrow */}
      <button
        aria-label="Scroll left"
        onClick={() => scrollBy(-120)}
        disabled={!canScrollLeft}
        className={`mr-1 p-1 rounded-full bg-blue-900 text-white transition-opacity duration-150 ${canScrollLeft ? 'opacity-100 hover:bg-blue-700' : 'opacity-40 cursor-not-allowed'}`}
        style={{ minWidth: 32 }}
      >
        {'\u25C0'}
      </button>
      {/* Scrollable nav */}
      <div
        ref={scrollContainerRef}
        onWheel={handleWheel}
        className="relative max-w-full overflow-x-auto scrollbar-hide transition-all duration-300 flex-1"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <style>{`.scrollbar-hide::-webkit-scrollbar{display:none}`}</style>
        <div className="flex space-x-1 p-0.5 min-w-max">
          {sections.map((section) => (
            <button
              key={section.id}
              ref={section.id === activeSection ? activeButtonRef : null}
              onClick={() => onSectionChange(section.id)}
              className={`group flex items-center px-2.5 py-1.5 rounded-md transition-all duration-150 whitespace-nowrap text-xs font-semibold border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-300 ${activeSection === section.id
                  ? 'bg-blue-600 text-white border-blue-400 shadow-sm'
                  : 'bg-blue-900/60 text-blue-100 hover:bg-blue-700/80 hover:text-white border-blue-800'
                }`}
              style={{ boxShadow: activeSection === section.id ? '0 2px 8px 0 rgba(59,130,246,0.10)' : undefined }}
            >
              <span className={`mr-1 text-base transition-transform duration-150 ${activeSection === section.id ? 'scale-105' : 'group-hover:scale-105'
                }`}>
                {section.icon}
              </span>
              <span className={`font-semibold ${activeSection === section.id ? 'text-white' : 'text-blue-100'
                }`}>
                {section.label}
              </span>
              {section.id === 'attestation_letter' && (
                <span className={`ml-1.5 px-1 py-0.5 text-[9px] font-bold rounded ${
                  isAttestationEnabled(reportData)
                    ? 'bg-emerald-500/30 text-emerald-200 border border-emerald-400/40'
                    : 'bg-white/10 text-blue-200 border border-white/20'
                }`}>
                  {isAttestationEnabled(reportData) ? 'ON' : 'OFF'}
                </span>
              )}
              {activeSection === section.id && (
                <span className="ml-1 w-1.5 h-1.5 bg-white/80 rounded-full animate-pulse border border-blue-300" />
              )}
            </button>
          ))}
        </div>
      </div>
      {/* Right Arrow */}
      <button
        aria-label="Scroll right"
        onClick={() => scrollBy(120)}
        disabled={!canScrollRight}
        className={`ml-1 p-1 rounded-full bg-blue-900 text-white transition-opacity duration-150 ${canScrollRight ? 'opacity-100 hover:bg-blue-700' : 'opacity-40 cursor-not-allowed'}`}
        style={{ minWidth: 32 }}
      >
        {'\u25B6'}
      </button>
    </div>
  );
}, (prev, next) => {
  return prev.activeSection === next.activeSection &&
    prev.userRole === next.userRole &&
    prev.onSectionChange === next.onSectionChange &&
    prev.reportData?.template === next.reportData?.template &&
    hasZeroFindings(prev.reportData) === hasZeroFindings(next.reportData) &&
    isAttestationEnabled(prev.reportData) === isAttestationEnabled(next.reportData);
});

export default EditorNavigation;